import { useAutofillFieldGroup } from '@modusign/react-signing';
import { AutofillFieldSupportFieldType, FieldNode } from '@modusign/react-signing/types';
import { useCallback, useMemo } from 'react';

import { ConfigurableAddressFieldRenderer } from '@app/features/apply-react-signing/components/FieldRenderer/Setup/FieldRenderers/ConfigurableAddressFieldRenderer';
import { ConfigurableCompanyNameFieldRenderer } from '@app/features/apply-react-signing/components/FieldRenderer/Setup/FieldRenderers/ConfigurableCompanyNameFieldRenderer';
import { ConfigurableNameFieldRenderer } from '@app/features/apply-react-signing/components/FieldRenderer/Setup/FieldRenderers/ConfigurableNameFieldRenderer';
import { ReactSigningFieldRendererProps } from '@app/features/apply-react-signing/components/FieldRenderer/types';
import { useOptimalFieldsContext } from '@app/features/apply-react-signing/libs/hooks/useCollectionOptimal';
import { isAutofillableField } from '@app/features/apply-react-signing/libs/isAutofillableField';

export type SignerFieldNodeType = Extract<FieldNode, { type: AutofillFieldSupportFieldType }>;

type ComponentByType<T extends AutofillFieldSupportFieldType> = {
  [K in T]: React.FunctionComponent<ConfigurableAutofillFieldGroupRendererProps>;
};

export interface ConfigurableAutofillFieldGroupRendererProps
  extends ReactSigningFieldRendererProps {
  onFieldRemove: (fieldId: string) => void;
  isDataLabelDuplicated: boolean;
}

const AutofillFieldComponentType: ComponentByType<AutofillFieldSupportFieldType> = {
  ADDRESS: ConfigurableAddressFieldRenderer,
  COMPANY_NAME: ConfigurableCompanyNameFieldRenderer,
  NAME: ConfigurableNameFieldRenderer,
};

export default function AutofillFieldGroupRenderer({
  fieldId,
  page,
  ...props
}: ReactSigningFieldRendererProps) {
  const {
    removeField,
    evaluated: { dataLabelDuplicationRecords },
  } = useOptimalFieldsContext<'AUTOFILL_GROUP', 'SIGNER'>();

  const { autofillFieldGroup, removeChild } = useAutofillFieldGroup(fieldId);
  const children = useMemo(() => {
    return autofillFieldGroup?.children
      .filter((child): child is SignerFieldNodeType => isAutofillableField(child))
      .filter((child) => {
        return child.position.page === page;
      });
  }, [page, autofillFieldGroup?.children]);

  const onFieldRemove = useCallback(
    (fieldId: string) => {
      if (autofillFieldGroup.children.length === 1) {
        removeField(autofillFieldGroup.id);
      } else {
        removeChild(fieldId);
      }
    },
    [removeChild, removeField, autofillFieldGroup.children.length, autofillFieldGroup.id]
  );

  return (
    <>
      {children.map((field) => {
        const FieldRenderer = AutofillFieldComponentType[field.type];
        return (
          <FieldRenderer
            {...props}
            fieldId={field.id}
            isDataLabelDuplicated={dataLabelDuplicationRecords[field.dataLabel] > 1}
            key={field.id}
            page={page}
            onFieldRemove={onFieldRemove}
          />
        );
      })}
    </>
  );
}
